import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, TextField, Button, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Divider, LinearProgress, Select, MenuItem, InputLabel, FormControl,
  InputAdornment, Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";
import { RemittanceDrawer } from "@/pages/admin/ZephyrRemittancePage";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const fmt = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";

function scriptStatusChip(s) {
  if (s.PaymentID) return <Chip label="Paid" size="small" sx={{ background: "#dcfce7", color: "#16a34a", fontSize: "0.72rem" }} />;
  if (Number(s.WrittenOff)) return <Chip label="Written Off" size="small" sx={{ background: "#fee2e2", color: "#dc2626", fontSize: "0.72rem" }} />;
  if (Number(s.OnHold) === -1) return <Chip label="Missed" size="small" sx={{ background: "#fef9c3", color: "#92400e", fontSize: "0.72rem" }} />;
  return null;
}

// ── Invoice Detail Modal ──────────────────────────────────────────
function InvoiceDetailModal({ open, invoiceID, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !invoiceID) return;
    setLoading(true);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_DETAILS}/${invoiceID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, invoiceID]);

  const c = data?.claim;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,

      }}>
        <span>Invoice #{invoiceID}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
        ) : (
          <>
            {c && (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Patient</Typography>
                  <Typography variant="body2" fontWeight={600}>{c.FullName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Chemist</Typography>
                  <Typography variant="body2">{c.ChemistName || c.ChemShortCode || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Date</Typography>
                  <Typography variant="body2">{fmtDate(c.InvoiceDate)}</Typography>
                </Box>
                {c.InvComment && (
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Comment</Typography>
                    <Typography variant="body2">{c.InvComment}</Typography>
                  </Box>
                )}
              </Box>
            )}
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                  <TableRow>
                    {["Item", "Qty", "Invoiced", "Status"].map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.scripts ?? []).map((s) => (
                    <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                      <TableCell>{s.txtItem}</TableCell>
                      <TableCell>{s.Qty ?? "—"}</TableCell>
                      <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                      <TableCell>{scriptStatusChip(s)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Payment Detail ────────────────────────────────────────────────
function PaymentDetail({ paymentID }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceDetailID, setInvoiceDetailID] = useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    setLoading(true);
    setError(null);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_PAYMENT_DETAILS}/${paymentID}`, "GET")
      .then((res) => {
        const d = res?.data ?? null;
        if (!d?.header) setError("Payment not found.");
        else setData(d);
      })
      .catch(() => setError("Payment not found."))
      .finally(() => setLoading(false));
  }, [paymentID]);

  const h = data?.header;

  return (
    <>
      {loading && <CircularProgress size={28} sx={{ color: Z_PURPLE }} />}
      {error && <Alert severity="warning" sx={{ maxWidth: 400 }}>{error}</Alert>}

      {data && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {h && (
            <Box sx={{
              background: `linear-gradient(135deg, ${Z_PURPLE}12, ${Z_BLUE}10)`,
              border: `1px solid ${Z_PURPLE}30`,
              borderRadius: 2, p: 2,
              display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5,
            }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Payment ID</Typography>
                <Typography fontWeight={700} sx={{ color: Z_PURPLE }}>#{paymentID}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Pay Date</Typography>
                <Typography variant="body2">{fmtDate(h.PayDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Insurer Pay Date</Typography>
                <Typography variant="body2">{fmtDate(h.InsCompPayDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Total Value</Typography>
                <Typography variant="body2" fontWeight={800} sx={{ color: "#16a34a" }}>{fmt(h.TotalPaymentValue)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Reference</Typography>
                <Typography variant="body2">{h.ReferenceNumber || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Type</Typography>
                <Typography variant="body2">{h.PaymentType || "—"}</Typography>
              </Box>
              {h.PaymentComment && (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>Comment</Typography>
                  <Typography variant="body2">{h.PaymentComment}</Typography>
                </Box>
              )}
            </Box>
          )}

          <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                <TableRow>
                  {[
                    { label: "Patient", align: "left" },
                    { label: "Invoice", align: "left" },
                    { label: "Item", align: "left" },
                    { label: "Date", align: "left" },
                    { label: "Invoiced", align: "right" },
                    { label: "Received", align: "right" },
                  ].map(({ label, align }) => (
                    <TableCell key={label} align={align}
                      sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.scripts ?? []).map((s) => (
                  <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1.5, fontSize: "0.8rem" } }}>
                    <TableCell>
                      <Tooltip title="Open patient">
                        <button
                          onClick={() => navigate("/zephyr-admin/customers", { state: { claimID: s.ClaimID } })}
                          style={{
                            background: "none", border: "none", cursor: "pointer", padding: 0,
                            color: Z_PURPLE, fontWeight: 600, textDecoration: "underline", fontSize: "inherit",
                          }}
                        >
                          {s.FullName}
                        </button>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => setInvoiceDetailID(s.InvoiceID)}
                        style={{
                          background: "none", border: "none", cursor: "pointer", padding: 0,
                          color: Z_PURPLE, fontWeight: 600, textDecoration: "underline", fontSize: "inherit",
                        }}
                      >
                        {s.InvoiceID}
                      </button>
                    </TableCell>
                    <TableCell>{s.txtItem}</TableCell>
                    <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                    <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                    <TableCell align="right">{fmt(s.RcvdAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <InvoiceDetailModal
        open={!!invoiceDetailID}
        invoiceID={invoiceDetailID}
        onClose={() => setInvoiceDetailID(null)}
      />
    </>
  );
}

// ── Pending Remittance Modal ──────────────────────────────────────────────────
function PendingRemittanceModal({ open, payRef, insCompID, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !payRef) return;
    setLoading(true);
    setData(null);
    phappsRequest(API_PATHS.ZEPHYR_ADMIN_AUTO_RECON_PENDING_REMITTANCE, "GET", null, { payRef, insCompID })
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, payRef, insCompID]);

  const h = data?.header;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        background: `linear-gradient(135deg, ${Z_PURPLE}10, ${Z_BLUE}08)`, pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ReceiptLongIcon sx={{ color: Z_PURPLE, fontSize: 20 }} />
          <Typography fontWeight={700} sx={{ color: Z_PURPLE }}>
            Remittance — Ref: {payRef ?? "…"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1,
          "& .MuiLinearProgress-bar": { background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` } }} />}

        {h && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            {[
              ["Insurer", h.InsCompName ?? (h.InsCompID ? `#${h.InsCompID}` : "—")],
              ["Insurer Pay Date", fmtDate(h.InsCompPayDate)],
              ["Total", fmt(h.TotalVal)],
            ].map(([label, val]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography fontWeight={600} fontSize="0.9rem">{val}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {(data?.invoices ?? []).map((inv, i) => (
          <Box key={inv.InvoiceID ?? i} sx={{ mb: 2, border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1, background: "#f8fafc" }}>
              <Typography fontWeight={700} fontSize="0.85rem" sx={{ color: Z_PURPLE }}>
                {inv.InvoiceID ? `Invoice #${inv.InvoiceID}` : "No Invoice ID"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[inv.PatientName, inv.ClaimNo ? `Claim ${inv.ClaimNo}` : null].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}06` }}>
                  <TableRow>
                    {["Item", "Script Date", "Invoiced", "Received"].map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.73rem", color: Z_PURPLE, py: 0.6 }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(inv.scripts ?? []).map((s, j) => (
                    <TableRow key={j} sx={{ "& td": { py: 0.4, fontSize: "0.8rem" } }}>
                      <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.TextImport || "—"}</TableCell>
                      <TableCell>{fmtDate(s.ScriptDate)}</TableCell>
                      <TableCell>{fmt(s.InvAmount)}</TableCell>
                      <TableCell>{fmt(s.RcvdAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button onClick={onClose} size="small" sx={{ textTransform: "none", color: Z_PURPLE }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Remittance Modal ──────────────────────────────────────────────────────────
function RemittanceModal({ open, logID, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !logID) return;
    setLoading(true);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_AUTO_RECON_REMITTANCE}/${logID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, logID]);

  const p = data?.payment;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        background: `linear-gradient(135deg, ${Z_PURPLE}10, ${Z_BLUE}08)`, pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ReceiptLongIcon sx={{ color: Z_PURPLE, fontSize: 20 }} />
          <Typography fontWeight={700} sx={{ color: Z_PURPLE }}>
            Remittance — Ref: {data?.payRef ?? "…"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1,
          "& .MuiLinearProgress-bar": { background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` } }} />}

        {p && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
            {[
              ["Insurer", p.InsCompName ?? "—"],
              ["Payment Date", fmtDate(p.PayDate)],
              ["Insurer Pay Date", fmtDate(p.InsCompPayDate)],
              ["Total", fmt(p.TotalPaymentValue)],
            ].map(([label, val]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography fontWeight={600} fontSize="0.9rem">{val}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {(data?.invoices ?? []).map((inv) => (
          <Box key={inv.InvoiceID} sx={{ mb: 2, border: `1px solid ${inv.mismatch ? "#fcd34d" : "#e2e8f0"}`,
            borderRadius: 1.5, overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1, background: inv.mismatch ? "#fefce8" : "#f8fafc",
              display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
              <Box>
                <Typography fontWeight={700} fontSize="0.85rem" sx={{ color: Z_PURPLE }}>
                  Invoice #{inv.InvoiceID}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {inv.PatientName ?? "—"} {inv.ClaimNumber ? `· Claim ${inv.ClaimNumber}` : ""}
                </Typography>
              </Box>
              {inv.mismatch && (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Chip label={`Expected ${fmt(inv.mismatch.ExpectedAmount)}`} size="small"
                    sx={{ background: "#f1f5f9", fontSize: "0.7rem" }} />
                  <Chip label={`Received ${fmt(inv.mismatch.ReceivedAmount)}`} size="small"
                    sx={{ background: "#fef9c3", color: "#92400e", fontSize: "0.7rem" }} />
                  <Chip label={inv.mismatch.Status} size="small"
                    sx={{ background: "#fee2e2", color: "#dc2626", fontSize: "0.7rem" }} />
                </Box>
              )}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}06` }}>
                  <TableRow>
                    {["Item", "Date", "Invoiced", "Received"].map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.73rem", color: Z_PURPLE, py: 0.6 }}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(inv.scripts ?? []).map((s) => (
                    <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.4, fontSize: "0.8rem" } }}>
                      <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.txtItem}</TableCell>
                      <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                      <TableCell>{fmt(s.InvAmount)}</TableCell>
                      <TableCell>
                        {s.OnHold === -1
                          ? <Chip label="On Hold" size="small" sx={{ background: "#fef9c3", color: "#92400e", fontSize: "0.7rem" }} />
                          : fmt(s.RcvdAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button onClick={onClose} size="small" sx={{ textTransform: "none", color: Z_PURPLE }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Mismatch Invoice Card ─────────────────────────────────────────────────────
function MismatchInvoiceCard({ item, onResolved }) {
  const scripts = item.scripts ?? [];
  const rcvdTotal = Number(item.ReceivedAmount ?? 0);
  const invTotal = Number(item.ExpectedAmount ?? 0);

  const [amounts, setAmounts] = useState(() => {
    if (item.ReceivedAmount == null) return Object.fromEntries(scripts.map((s) => [s.ScriptID, ""]));
    const totalInv = scripts.reduce((sum, s) => sum + Number(s.InvAmount ?? 0), 0);
    return Object.fromEntries(scripts.map((s) => {
      const share = totalInv > 0
        ? (Number(s.InvAmount ?? 0) / totalInv) * rcvdTotal
        : rcvdTotal / (scripts.length || 1);
      return [s.ScriptID, share.toFixed(2)];
    }));
  });
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState(null);
  const [remittanceOpen, setRemittanceOpen] = useState(false);
  const [heldScripts, setHeldScripts] = useState(new Set());

  const toggleHold = (scriptID) => setHeldScripts((prev) => {
    const next = new Set(prev);
    next.has(scriptID) ? next.delete(scriptID) : next.add(scriptID);
    return next;
  });

  const totalEntered = Object.entries(amounts).reduce((s, [id, v]) => {
    if (heldScripts.has(Number(id))) return s;
    return s + (v !== "" ? Number(v) : 0);
  }, 0);

  const resolve = async (decision) => {
    setResolving(true);
    setError(null);
    try {
      const scriptAmounts = decision !== "skip" && decision !== "on_hold"
        ? scripts.map((s) => ({
            scriptID: s.ScriptID,
            rcvdAmount: heldScripts.has(s.ScriptID) ? 0 : Number(amounts[s.ScriptID] ?? 0),
            onHold: heldScripts.has(s.ScriptID),
          }))
        : undefined;
      await phappsRequest(API_PATHS.ZEPHYR_ADMIN_AUTO_RECON_RESOLVE, "POST", { logID: item.LogID, decision, scripts: scriptAmounts });
      onResolved(item.LogID);
    } catch {
      setError("Failed to resolve. Please try again.");
    } finally {
      setResolving(false);
    }
  };

  return (
    <>
    <RemittanceModal open={remittanceOpen} logID={item.LogID} onClose={() => setRemittanceOpen(false)} />
    <Box sx={{ border: `1.5px solid ${Z_PURPLE}25`, borderRadius: 2, overflow: "hidden" }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}10, ${Z_BLUE}08)`,
        px: 2, py: 1.5,
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <WarningAmberIcon sx={{ color: "#d97706", fontSize: 18 }} />
          <Box>
            <Typography fontWeight={700} fontSize="0.9rem" sx={{ color: Z_PURPLE }}>
              Invoice #{item.InvoiceID}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.InsCompName || `Insurer #${item.InsCompID}`} — Ref: {item.PayRef} — Payment #{item.LinkedPaymentID}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Tooltip title="View full remittance">
            <IconButton size="small" onClick={() => setRemittanceOpen(true)}
              sx={{ color: Z_PURPLE, border: `1px solid ${Z_PURPLE}30`, borderRadius: 1 }}>
              <ReceiptLongIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Chip label={`Expected ${fmt(invTotal)}`} size="small" sx={{ background: "#f1f5f9", color: "#475569", fontSize: "0.72rem" }} />
          <Chip label={`Received ${fmt(rcvdTotal)}`} size="small" sx={{ background: "#fef9c3", color: "#92400e", fontSize: "0.72rem" }} />
          <Chip label={`Variance ${fmt(rcvdTotal - invTotal)}`} size="small"
            sx={{ background: rcvdTotal < invTotal ? "#fee2e2" : "#dcfce7", color: rcvdTotal < invTotal ? "#dc2626" : "#16a34a", fontSize: "0.72rem" }} />
        </Box>
      </Box>

      {scripts.length > 0 ? (
        <TableContainer sx={{ borderTop: "1px solid #f1f5f9" }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: `${Z_PURPLE}06` }}>
              <TableRow>
                {["Item", "Date", "Invoiced", "Received ($)", ""].map((col) => (
                  <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.74rem", color: Z_PURPLE, py: 0.75 }}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {scripts.map((s) => {
                const held = heldScripts.has(s.ScriptID);
                return (
                  <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.5, px: 1.5, fontSize: "0.8rem" },
                    background: held ? "#fffbeb" : "transparent" }}>
                    <TableCell sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.txtItem}</TableCell>
                    <TableCell>{s.DateDispensed || "—"}</TableCell>
                    <TableCell>{fmt(s.InvAmount)}</TableCell>
                    <TableCell>
                      {held ? (
                        <Chip label="On Hold" size="small" sx={{ background: "#fef3c7", color: "#92400e", fontSize: "0.7rem" }} />
                      ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <TextField
                            size="small" type="number" placeholder="0.00"
                            value={amounts[s.ScriptID] ?? ""}
                            onChange={(e) => setAmounts((p) => ({ ...p, [s.ScriptID]: e.target.value }))}
                            inputProps={{ min: 0, step: "0.01" }}
                            sx={{ width: 100, "& input": { py: 0.4, fontSize: "0.8rem" } }}
                          />
                          <Tooltip title="Set to invoice amount">
                            <Button size="small" variant="outlined"
                              onClick={() => setAmounts((p) => ({ ...p, [s.ScriptID]: Number(s.InvAmount).toFixed(2) }))}
                              sx={{ minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.68rem", color: Z_BLUE, borderColor: Z_BLUE, textTransform: "none" }}>
                              = Inv
                            </Button>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: "2px !important" }}>
                      <Tooltip title={held ? "Clear hold" : "Mark as on hold"}>
                        <Button size="small" variant={held ? "contained" : "outlined"}
                          onClick={() => toggleHold(s.ScriptID)}
                          sx={{
                            minWidth: 0, px: 0.75, py: 0.25, fontSize: "0.68rem", textTransform: "none",
                            ...(held
                              ? { background: "#d97706", borderColor: "#d97706", color: "#fff", "&:hover": { background: "#b45309" } }
                              : { color: "#d97706", borderColor: "#d97706" }),
                          }}>
                          {held ? "↩ Clear" : "Hold"}
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" color="text.secondary" fontSize="0.8rem">No unpaid scripts found for this invoice.</Typography>
        </Box>
      )}

      <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {[
            ["Total Entered", fmt(totalEntered), Z_PURPLE],
            [Math.abs(totalEntered - invTotal) < 0.005 ? "Balanced" : totalEntered < invTotal ? "Shortfall" : "Overpaid",
              Math.abs(totalEntered - invTotal) < 0.005 ? "✓" : fmt(Math.abs(totalEntered - invTotal)),
              Math.abs(totalEntered - invTotal) < 0.005 ? "#16a34a" : "#dc2626"],
          ].map(([label, val, color]) => (
            <Chip key={label} label={`${label}: ${val}`} size="small"
              sx={{ background: "#f8fafc", color, fontWeight: 700, fontSize: "0.72rem" }} />
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {error && <Typography color="error" fontSize="0.78rem">{error}</Typography>}
          {resolving && <CircularProgress size={18} sx={{ color: Z_PURPLE }} />}
          <Button size="small" variant="outlined"
            onClick={() => resolve("on_hold")}
            disabled={resolving}
            sx={{ textTransform: "none", fontSize: "0.8rem", borderColor: "#d97706", color: "#d97706" }}>
            On Hold
          </Button>
          <Button size="small" variant="contained"
            onClick={() => resolve("manual")}
            disabled={resolving || scripts.length === 0}
            sx={{ textTransform: "none", fontSize: "0.8rem", background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` }}>
            Accept
          </Button>
          <Button size="small" variant="outlined" color="error"
            onClick={() => resolve("skip")}
            disabled={resolving}
            sx={{ textTransform: "none", fontSize: "0.8rem" }}>
            Skip
          </Button>
        </Box>
      </Box>
    </Box>
    </>
  );
}

// ── Auto-Reconcile Section ────────────────────────────────────────────────────
function AutoReconcileSection({ onProcessed }) {
  const [pending, setPending] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [reviewItems, setReviewItems] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [processError, setProcessError] = useState(null);
  const [pendingViewTarget, setPendingViewTarget] = useState(null);

  const loadPending = useCallback(() => {
    setPendingLoading(true);
    phappsRequest(API_PATHS.ZEPHYR_ADMIN_AUTO_RECON_PENDING, "GET")
      .then((res) => setPending(res?.data ?? null))
      .catch(() => setPending(null))
      .finally(() => setPendingLoading(false));
  }, []);

  const loadReview = useCallback(() => {
    setReviewLoading(true);
    phappsRequest(API_PATHS.ZEPHYR_ADMIN_AUTO_RECON_PENDING_REVIEW, "GET")
      .then((res) => setReviewItems(res?.data ?? []))
      .catch(() => setReviewItems([]))
      .finally(() => setReviewLoading(false));
  }, []);

  useEffect(() => {
    loadPending();
    loadReview();
  }, [loadPending, loadReview]);

  const handleProcess = async () => {
    setProcessing(true);
    setProcessError(null);
    setProcessResult(null);
    try {
      const res = await phappsRequest(API_PATHS.ZEPHYR_ADMIN_AUTO_RECON_PROCESS, "POST");
      setProcessResult(res?.data ?? null);
      loadPending();
      loadReview();
      onProcessed?.();
    } catch {
      setProcessError("Processing failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const handleResolved = (logID) => {
    setReviewItems((prev) => prev.filter((i) => i.LogID !== logID));
  };

  const summary = pending?.summary;
  const remittances = pending?.remittances ?? [];
  const hasPending = summary && summary.remittanceCount > 0;
  const hasReview = reviewItems.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PendingRemittanceModal
        open={!!pendingViewTarget}
        payRef={pendingViewTarget?.payRef ?? null}
        insCompID={pendingViewTarget?.insCompID ?? null}
        onClose={() => setPendingViewTarget(null)}
      />
      <Divider />

      {/* Section Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: 1.5,
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <AutoFixHighIcon sx={{ color: "#fff", fontSize: 18 }} />
        </Box>
        <Typography fontWeight={700} fontSize="1rem" sx={{ color: Z_PURPLE }}>Auto-Reconcile</Typography>
      </Box>

      {/* Pending Queue Card */}
      <Card variant="outlined" sx={{ borderRadius: 2, border: `1.5px solid ${Z_PURPLE}30` }}>
        <CardContent sx={{ p: 2.5 }}>
          {pendingLoading ? (
            <LinearProgress sx={{ borderRadius: 1, "& .MuiLinearProgress-bar": { background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` } }} />
          ) : !hasPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 22 }} />
              <Typography variant="body2" color="text.secondary">No pending remittances to process.</Typography>
              <Button size="small" onClick={loadPending} sx={{ ml: "auto", textTransform: "none", color: Z_PURPLE, fontSize: "0.78rem" }}>Refresh</Button>
            </Box>
          ) : (
            <>
              {/* Summary chips */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                {[
                  [`${summary.remittanceCount} remittance${summary.remittanceCount !== 1 ? "s" : ""}`, `${Z_PURPLE}12`],
                  [`${summary.invoiceCount} invoice${summary.invoiceCount !== 1 ? "s" : ""}`, `${Z_BLUE}15`],
                  [fmt(summary.totalValue), "#dcfce7"],
                ].map(([label, bg]) => (
                  <Chip key={label} label={label} size="small" sx={{ background: bg, fontWeight: 700, fontSize: "0.78rem" }} />
                ))}
                <Button size="small" onClick={loadPending} sx={{ ml: "auto", textTransform: "none", color: Z_PURPLE, fontSize: "0.78rem" }}>Refresh</Button>
              </Box>

              {/* Remittances table */}
              <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, mb: 2 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                    <TableRow>
                      {["Pay Ref", "Insurer", "Pay Date", "Invoices", "Total", ""].map((col) => (
                        <TableCell key={col} sx={{ fontWeight: 700, fontSize: "0.76rem", color: Z_PURPLE, py: 0.75 }}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {remittances.map((r) => (
                      <TableRow key={`${r.PayRef}-${r.InsCompID}`} sx={{ "& td": { py: 0.6, px: 1.5, fontSize: "0.8rem" } }}>
                        <TableCell sx={{ fontWeight: 600, color: Z_PURPLE }}>{r.PayRef}</TableCell>
                        <TableCell>{r.InsCompName || `#${r.InsCompID}`}</TableCell>
                        <TableCell>{r.PayDate || "—"}</TableCell>
                        <TableCell>{Number(r.InvoiceCount)}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#16a34a" }}>{fmt(r.TotalVal)}</TableCell>
                        <TableCell sx={{ py: "2px !important" }}>
                          <Tooltip title="View remittance">
                            <IconButton size="small" onClick={() => setPendingViewTarget({ payRef: r.PayRef, insCompID: r.InsCompID })}
                              sx={{ color: Z_PURPLE, opacity: 0.7, "&:hover": { opacity: 1 } }}>
                              <ReceiptLongIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button
                variant="contained"
                onClick={handleProcess}
                disabled={processing}
                startIcon={processing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <AutoFixHighIcon />}
                sx={{
                  textTransform: "none", fontWeight: 700,
                  background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
                  "&:hover": { background: `linear-gradient(135deg, #1a0a2e, ${Z_PURPLE})` },
                  "&:disabled": { background: "#94a3b8" },
                }}
              >
                {processing ? "Processing…" : "Auto Process All"}
              </Button>
            </>
          )}

          {processError && (
            <Alert severity="error" sx={{ mt: 2 }}>{processError}</Alert>
          )}

          {processResult && (
            <Box sx={{ mt: 2, p: 2, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 1.5 }}>
              <Typography fontWeight={700} fontSize="0.9rem" sx={{ color: "#16a34a", mb: 0.5 }}>
                Processing complete
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {processResult.processedPayments?.length ?? 0} payment{processResult.processedPayments?.length !== 1 ? "s" : ""} created
                {processResult.newMismatches > 0 && ` · ${processResult.newMismatches} invoice${processResult.newMismatches !== 1 ? "s" : ""} need review below`}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Review Panel */}
      {(reviewLoading || hasReview) && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <WarningAmberIcon sx={{ color: "#d97706", fontSize: 18 }} />
            <Typography fontWeight={700} fontSize="0.95rem" sx={{ color: "#92400e" }}>
              Invoice Mismatches — Review Required
              {!reviewLoading && ` (${reviewItems.length})`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Enter amounts per script and accept, or skip to log without processing.
            </Typography>
          </Box>

          {reviewLoading ? (
            <LinearProgress sx={{ borderRadius: 1 }} />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {reviewItems.map((item) => (
                <MismatchInvoiceCard key={item.LogID} item={item} onResolved={handleResolved} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Recent Payments Panel ─────────────────────────────────────────────────────
function RecentPaymentsPanel({ onOpenPayment, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterInsurer, setFilterInsurer] = useState("");
  const [filterRef, setFilterRef] = useState("");
  const [filterMinVal, setFilterMinVal] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    phappsRequest(API_PATHS.ZEPHYR_ADMIN_RECENT_PAYMENTS, "GET")
      .then((res) => setRows(res?.data ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]);

  const insurers = useMemo(() => {
    const names = [...new Set(rows.map((r) => r.InsCompName).filter(Boolean))].sort();
    return names;
  }, [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (filterInsurer && r.InsCompName !== filterInsurer) return false;
    if (filterRef && !(r.ReferenceNumber ?? "").toLowerCase().includes(filterRef.toLowerCase())) return false;
    if (filterMinVal !== "" && Number(r.TotalPaymentValue ?? 0) < Number(filterMinVal)) return false;
    return true;
  }), [rows, filterInsurer, filterRef, filterMinVal]);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, border: `1.5px solid ${Z_PURPLE}30`, height: "100%" }}>
      <CardContent sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: 1.5,
            background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ReceiptLongIcon sx={{ color: "#fff", fontSize: 16 }} />
          </Box>
          <Typography fontWeight={700} fontSize="0.95rem">Recent Payments</Typography>
          <Chip label="15 days" size="small" sx={{ ml: 0.5, fontSize: "0.7rem", background: `${Z_PURPLE}12`, color: Z_PURPLE }} />
          <Button size="small" onClick={load} disabled={loading}
            sx={{ ml: "auto", textTransform: "none", color: Z_PURPLE, fontSize: "0.75rem", minWidth: 0 }}>
            Refresh
          </Button>
        </Box>

        {loading ? (
          <LinearProgress sx={{ borderRadius: 1,
            "& .MuiLinearProgress-bar": { background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` } }} />
        ) : (
          <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, maxHeight: 520, overflow: "auto" }}>
            <Table size="small" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <TableHead>
                {/* Filter row — each input sits in its column cell */}
                <TableRow sx={{ "& th": { background: "#fff", borderBottom: "1px solid #e2e8f0", py: "4px", position: "sticky", top: 0, zIndex: 4 } }}>
                  <TableCell /> {/* Date — no filter */}
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={insurers}
                      value={filterInsurer || null}
                      onChange={(_, val) => setFilterInsurer(val ?? "")}
                      clearOnEscape
                      renderInput={(params) => (
                        <TextField {...params} label="Insurer" sx={{ "& input": { fontSize: "0.75rem" } }} />
                      )}
                      sx={{ "& .MuiAutocomplete-input": { fontSize: "0.75rem" } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" label="Reference" value={filterRef} fullWidth
                      onChange={(e) => setFilterRef(e.target.value)}
                      sx={{ "& input": { fontSize: "0.75rem" } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" label="Min $" value={filterMinVal} type="number" fullWidth
                      onChange={(e) => setFilterMinVal(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: "0.75rem" }}>$</InputAdornment> }}
                      sx={{ "& input": { fontSize: "0.75rem" } }}
                    />
                  </TableCell>
                  <TableCell /> {/* Action — no filter */}
                </TableRow>
                {/* Column headers */}
                <TableRow sx={{ "& th": { background: "#ede9f3", fontWeight: 700, fontSize: "0.73rem", color: Z_PURPLE, py: 0.75, position: "sticky", top: "48px", zIndex: 3 } }}>
                  <TableCell sx={{ width: 80 }}>Date</TableCell>
                  <TableCell sx={{ width: "40%" }}>Insurer</TableCell>
                  <TableCell sx={{ width: 110 }}>Reference</TableCell>
                  <TableCell align="right" sx={{ width: 130 }}>Total</TableCell>
                  <TableCell sx={{ width: 36 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 2, fontSize: "0.8rem" }}>
                      No payments in the last 15 days.
                    </TableCell>
                  </TableRow>
                ) : filtered.map((r) => (
                  <TableRow key={r.PaymentID} sx={{ "& td": { py: 0.5, px: 1.5, fontSize: "0.78rem" },
                    "&:hover": { background: `${Z_PURPLE}05` } }}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.PayDate)}</TableCell>
                    <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.InsCompName || "—"}
                    </TableCell>
                    <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.ReferenceNumber || "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: "#16a34a", whiteSpace: "nowrap" }}>
                      {fmt(r.TotalPaymentValue)}
                    </TableCell>
                    <TableCell sx={{ py: "2px !important" }}>
                      <Tooltip title="View payment">
                        <IconButton size="small" onClick={() => onOpenPayment(r.PaymentID)}
                          sx={{ color: Z_PURPLE, opacity: 0.7, "&:hover": { opacity: 1 } }}>
                          <SearchIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default function ZephyrPaymentsPage() {
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [paymentSearch, setPaymentSearch] = useState("");
  const [activePaymentID, setActivePaymentID] = useState(null);
  const [paymentsRefreshKey, setPaymentsRefreshKey] = useState(0);
  const refreshPayments = useCallback(() => setPaymentsRefreshKey((k) => k + 1), []);

  const handleOpen = () => {
    const id = Number(paymentSearch);
    if (!id) return;
    setActivePaymentID(id);
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5,
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <img src={ZephyrIconWhite} alt="" style={{ height: 20, objectFit: "contain" }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.25rem" sx={{ color: Z_PURPLE }}>Payments</Typography>
      </Box>

      {/* Two-column layout: left 66% / right 33% */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, gap: 3, alignItems: "start" }}>

        {/* ── Left column ─────────────────────────────────────── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Top row: Reconcile + Open Payment side by side */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            {/* Reconcile */}
            <Card variant="outlined" sx={{ borderRadius: 2, border: `1.5px solid ${Z_PURPLE}30` }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <AddCircleOutlineIcon sx={{ color: "#fff", fontSize: 20 }} />
                  </Box>
                  <Typography fontWeight={700} fontSize="0.95rem">Reconcile Payment</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Match a received EFT against outstanding invoices.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setReconcileOpen(true)}
                  sx={{
                    textTransform: "none", fontWeight: 700,
                    background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
                    "&:hover": { background: `linear-gradient(135deg, #1a0a2e, ${Z_PURPLE})` },
                  }}
                >
                  Reconcile Payment
                </Button>
              </CardContent>
            </Card>

            {/* Open Payment */}
            <Card variant="outlined" sx={{ borderRadius: 2, border: "1.5px solid #e2e8f0" }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 1.5, background: "#f1f5f9",
                    display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <SearchIcon sx={{ color: "#64748b", fontSize: 20 }} />
                  </Box>
                  <Typography fontWeight={700} fontSize="0.95rem">Open Payment</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  View scripts and details for a specific payment.
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    label="Payment ID"
                    size="small"
                    type="number"
                    value={paymentSearch}
                    onChange={(e) => setPaymentSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleOpen()}
                    sx={{ width: 160 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleOpen}
                    disabled={!paymentSearch}
                    sx={{ textTransform: "none", borderColor: Z_PURPLE, color: Z_PURPLE }}
                  >
                    Open
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Payment Detail (shown when a payment is opened) */}
          {activePaymentID && (
            <Box>
              <Typography fontWeight={700} sx={{ color: Z_PURPLE, mb: 1.5 }}>Payment #{activePaymentID}</Typography>
              <PaymentDetail key={activePaymentID} paymentID={activePaymentID} />
            </Box>
          )}

          {/* Auto-Reconcile Section */}
          <AutoReconcileSection onProcessed={refreshPayments} />
        </Box>

        {/* ── Right column: Recent Payments ───────────────────── */}
        <RecentPaymentsPanel
          onOpenPayment={(id) => { setActivePaymentID(id); setPaymentSearch(String(id)); }}
          refreshKey={paymentsRefreshKey}
        />
      </Box>

      <RemittanceDrawer open={reconcileOpen} onClose={() => { setReconcileOpen(false); refreshPayments(); }} />
    </Box>
  );
}
